import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Country, State, City } from 'country-state-city';
import { MapPin, Search, ChevronRight, X } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import { LocationContext } from '../../contexts/LocationContext';
import './LocationModal.css';

const LocationModal = ({ isOpen, onClose }) => {
  const { updateLocation } = useContext(LocationContext);
  const [selectedCountry, setSelectedCountry] = useState('IN');
  const [selectedState, setSelectedState] = useState('');
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [displayLocation, setDisplayLocation] = useState('Select Location');
  const [detecting, setDetecting] = useState(false);
  const [selectedCountryName, setSelectedCountryName] = useState('India');
  const [selectedStateName, setSelectedStateName] = useState('');

  const popularCities = [
    { name: 'Mumbai', state: 'Maharashtra', stateCode: 'MH', icon: '/city/mumbai.png' },
    { name: 'Delhi', state: 'Delhi', stateCode: 'DL', icon: '/city/delhi.png' },
    { name: 'Bengaluru', state: 'Karnataka', stateCode: 'KA', icon: '/city/bengaluru.png' },
    { name: 'Hyderabad', state: 'Telangana', stateCode: 'TG', icon: '/city/hyderabad.png' },
    { name: 'Ahmedabad', state: 'Gujarat', stateCode: 'GJ', icon: '/city/ahmedabad.png' },
    { name: 'Chennai', state: 'Tamil Nadu', stateCode: 'TN', icon: '/city/chennai.png' },
    { name: 'Kolkata', state: 'West Bengal', stateCode: 'WB', icon: '/city/kolkata.png' },
    { name: 'Pune', state: 'Maharashtra', stateCode: 'MH', icon: '/city/pune.png' },
  ];

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(() => {
    return selectedCountry ? State.getStatesOfCountry(selectedCountry) : [];
  }, [selectedCountry]);

  useEffect(() => {
    const country = countries.find(c => c.isoCode === selectedCountry);
    setSelectedCountryName(country?.name || 'India');
  }, [selectedCountry, countries]);

  useEffect(() => {
    setLoading(true);
    let cityList = [];
    if (selectedState) {
      cityList = City.getCitiesOfState(selectedCountry, selectedState);
      const state = states.find(s => s.isoCode === selectedState);
      setSelectedStateName(state?.name || '');
    } else {
      cityList = City.getCitiesOfCountry(selectedCountry);
      setSelectedStateName('');
    }
    if (cityList && cityList.length > 0) {
      cityList.sort((a, b) => a.name.localeCompare(b.name));
      setCities(cityList);
      setFilteredCities(cityList);
    } else {
      setCities([]);
      setFilteredCities([]);
    }
    setLoading(false);
  }, [selectedCountry, selectedState, states]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredCities(cities);
    } else {
      const lowerQuery = searchTerm.toLowerCase();
      const filtered = cities.filter(c => c.name.toLowerCase().includes(lowerQuery));
      setFilteredCities(filtered);
    }
  }, [searchTerm, cities]);

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    setSelectedCountry(countryCode);
    setSelectedState('');
    setSearchTerm('');
  };

  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    setSelectedState(stateCode);
    setSearchTerm('');
  };

  const detectLocation = () => {
    if (!navigator.geolocation) return alert('Geolocation not supported');
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        const res = await fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
        );
        const data = await res.json();
        const detectedCountryCode = data.countryCode;
        if (detectedCountryCode) {
          setSelectedCountry(detectedCountryCode);
        }
        handleSelect(data.city || data.locality, data.principalSubdivision, detectedCountryCode);
      } catch (err) {
        console.error(err);
        alert('Could not detect address.');
      } finally {
        setDetecting(false);
      }
    }, () => {
      setDetecting(false);
      alert('Location access denied');
    });
  };

  const handleSelect = (cityName, stateName, countryCode = selectedCountry) => {
    const country = countries.find(c => c.isoCode === countryCode);
    const cName = country?.name || selectedCountryName;
    const sName = stateName || selectedStateName;
    const fullLocation = `${cityName}, ${sName ? sName + ', ' : ''}${cName}`;
    setDisplayLocation(fullLocation);
    const locationData = { 
      city: cityName, 
      state: sName, 
      country: cName,
      countryCode: countryCode
    };
    updateLocation(locationData);
    localStorage.setItem('userLocation', JSON.stringify(locationData));
    handleClose();
  };

  const handleClose = () => {
    const container = document.querySelector('.location-modal-container');
    if (container && window.innerWidth <= 576) {
      container.style.animation = 'slideDown 0.3s ease-out';
      setTimeout(() => {
        onClose();
        setSearchTerm('');
      }, 300);
    } else {
      onClose();
      setSearchTerm('');
    }
  };

  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        setDisplayLocation(`${location.city}, ${location.state ? location.state + ', ' : ''}${location.country}`);
        updateLocation(location);
      } catch (err) {
        console.error('Error loading saved location:', err);
      }
    }
  }, []);

  const handleTouchStart = (e) => {
    if (window.innerWidth <= 576) {
      const filtersContainer = document.querySelector('.filters-container');
      if (filtersContainer && filtersContainer.contains(e.target)) return;
      window.touchStartY = e.touches[0].clientY;
    }
  };

  useEffect(() => {
    const handleTouchMove = (e) => {
      if (!isOpen || window.innerWidth > 576 || !window.touchStartY) return;
      const filtersContainer = document.querySelector('.filters-container');
      const searchContainer = document.querySelector('.search-container');
      if ((filtersContainer && filtersContainer.contains(e.target)) || 
          (searchContainer && searchContainer.contains(e.target))) return;
      const diff = e.touches[0].clientY - window.touchStartY;
      if (diff > 80) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('touchmove', handleTouchMove, { passive: true });
    }
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      const scrollBody = document.querySelector('.modal-scroll-body');
      if (scrollBody) {
        scrollBody.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });
      }
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  return (
    <Modal 
      show={isOpen} 
      onHide={handleClose} 
      centered 
      dialogClassName="custom-location-modal"
      contentClassName="location-modal-content"
      backdrop="static"
      keyboard={false}
    >
        <div className="location-modal-container" onTouchStart={handleTouchStart}>
          <div className="modal-header-custom">
            <h5>Select Location</h5>
            <button onClick={handleClose} className="close-btn">
              <X size={20} />
            </button>
          </div>

          <div className="filters-container" onClick={(e) => e.stopPropagation()}>
            <div className="filter-group">
              <label>Country</label>
              <select value={selectedCountry} onChange={handleCountryChange} className="filter-select" onClick={(e) => e.stopPropagation()}>
                {countries.map(country => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {states.length > 0 && (
              <div className="filter-group">
                <label>State</label>
                <select value={selectedState} onChange={handleStateChange} className="filter-select" onClick={(e) => e.stopPropagation()}>
                  <option value="">All States</option>
                  {states.map(state => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="search-container">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search city, area or locality"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="modal-scroll-body">
            <div className="current-location-row" onClick={detectLocation}>
              <div className="icon-box">
                <img src="/city/gps.png" alt="GPS" />
              </div>
              <div className="location-details">
                <span className="primary-text">Use Current Location</span>
                <span className="secondary-text">{detecting ? 'Detecting...' : 'Using GPS'}</span>
              </div>
            </div>

            {!searchTerm && !selectedState && (
              <div className="popular-section">
                <h6>Popular Cities</h6>
                <div className="popular-grid">
                  {popularCities.map((city, idx) => (
                    <div
                      key={idx}
                      className="city-card"
                      onClick={() => handleSelect(city.name, city.state)}
                    >
                      <div className="city-img-wrapper">
                        <img src={city.icon} alt={city.name} />
                      </div>
                      <span className="city-name">{city.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="all-cities-section">
              <h6>{searchTerm ? 'Search Results' : 'All Cities'}</h6>
              {loading ? (
                <div className="loading-state">Loading cities...</div>
              ) : (
                <ul className="cities-list">
                  {filteredCities.length > 0 ? (
                    filteredCities.map((city, idx) => (
                      <li
                        key={`${city.name}-${idx}`}
                        onClick={() => handleSelect(city.name, selectedStateName)}
                        className="city-list-item"
                      >
                        <MapPin size={16} className="list-icon" />
                        <span className="city-name">{city.name}</span>
                      </li>
                    ))
                  ) : (
                    <div className="empty-state">No cities found</div>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="modal-close-area" onClick={handleClose}></div>
      </Modal>
    );
  };
  
  export default LocationModal;
